import React, { CSSProperties, FC, useEffect, useMemo } from 'react';
import { useDrag, DragSourceMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DndType } from 'src/types/dnd';
import useSelectedCallOffs from 'src/reactiveVars/SelectedCallOffsVar';
import { ICallOff, ICallOffsDndItem } from 'src/models/callOffs/types';
import { BidItemType } from '@tendium/prom-types/tender';
import { notUndefined } from 'src/helpers/typescript';

interface IProps {
  record: ICallOff;
  className?: string;
  style?: CSSProperties;
  [x: string]: unknown;
}

export const CallOffsDrag: FC<IProps> = ({ record, className, style, ...restProps }: IProps) => {
  const [selectedCallOffs] = useSelectedCallOffs();
  const originIds = useMemo(() => {
    if (!!selectedCallOffs.length) {
      return selectedCallOffs.filter(callOff => !callOff.bid).map(callOff => callOff.id);
    } else if (record.bid) {
      return;
    } else {
      return [record.id];
    }
  }, [record.bid, record.id, selectedCallOffs]);

  const bidIds = useMemo(() => {
    if (!!selectedCallOffs.length) {
      return selectedCallOffs.map(callOff => callOff.bid).filter(notUndefined);
    } else if (record.bid) {
      return [record.bid];
    } else {
      return;
    }
  }, [record.bid, selectedCallOffs]);

  const linkedBids = useMemo(() => {
    if (!!selectedCallOffs.length) {
      return selectedCallOffs.map(callOff => callOff.linkedBid).filter(notUndefined);
    } else if (record.linkedBid) {
      return [record.linkedBid];
    } else {
      return;
    }
  }, [record.linkedBid, selectedCallOffs]);

  const dndCallOffsItem: ICallOffsDndItem = useMemo(() => {
    return {
      type: DndType.CallOffsItem,
      dndItem: record,
      bidIds,
      originIds,
      originType: BidItemType.CallOff,
      linkedBids
    };
  }, [bidIds, linkedBids, originIds, record]);

  const [{ opacity }, drag, preview] = useDrag({
    item: dndCallOffsItem,

    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
      item: monitor.getItem(),
      opacity: monitor.isDragging() ? 0.4 : 1
    })
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return <tr ref={drag} className={className} style={{ cursor: 'move', opacity, ...style }} {...restProps} />;
};

const RowComponent: FC<IProps> = props => {
  const { record } = props;
  if (record) {
    return <CallOffsDrag {...props} />;
  } else {
    return <tr {...props} />;
  }
};

export default RowComponent;
